import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import PageHeader from "@/components/feature/PageHeader";
import FaqAccordion from "@/components/base/FaqAccordion";
import { faqs } from "@/mocks/faqs";
import { site } from "@/mocks/site";

const groupIcons: Record<string, string> = {
  选群入门: "ri-compass-3-line",
  活跃度判断: "ri-pulse-line",
  避坑指南: "ri-shield-check-line",
  使用建议: "ri-lightbulb-line",
};

export default function Faq() {
  const grouped = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, typeof faqs>();
    faqs.forEach((item) => {
      if (!map.has(item.group)) {
        map.set(item.group, []);
        order.push(item.group);
      }
      map.get(item.group)?.push(item);
    });
    return order.map((name) => ({ name, items: map.get(name) ?? [] }));
  }, []);

  const jsonLd = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: "常见问题", item: `${origin}/faq` },
        ],
      },
    ];
  }, []);

  useSeo({
    title: "常见问题 - 51吃瓜群怎么选、怎么避坑的十问十答",
    description:
      "关于51吃瓜群的常见问题汇总：什么是吃瓜群、第一次该选哪种、怎么判断冷清群、活跃度数据怎么来、遇到收费群怎么办、未证实爆料如何处理、同时加多个群如何管理，一次讲清楚。",
    keywords: "51吃瓜群常见问题,吃瓜群怎么选,吃瓜群避坑,吃瓜群收费,冷清群判断",
    path: "/faq",
    jsonLd,
  });

  return (
    <>
      <PageHeader
        eyebrow="十问十答"
        title="关于选群，你可能想知道的都在这里"
        desc="从入门选择、活跃度判断，到避坑与日常使用建议，我们把瓜友问得最多的问题按主题整理成了四个部分。"
        crumbs={[{ label: "首页", to: "/" }, { label: "常见问题" }]}
      />

      <section className="w-full bg-background-50 px-4 py-10 md:px-6 md:py-14">
        <div className="mx-auto flex max-w-4xl flex-col gap-10">
          {grouped.map((section, index) => (
            <div key={section.name} id={`faq-${index + 1}`} className="scroll-mt-24">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${groupIcons[section.name] ?? "ri-question-line"} text-lg`}></i>
                </span>
                <div>
                  <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">{section.name}</h2>
                  <p className="text-xs text-foreground-500">{section.items.length} 个问题</p>
                </div>
              </div>
              <div className="mt-5">
                <FaqAccordion items={section.items} />
              </div>
            </div>
          ))}

          <div className="flex flex-col items-start justify-between gap-5 rounded-lg border border-background-200 bg-background-100 p-6 md:flex-row md:items-center md:p-8">
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className="ri-mail-send-line text-xl"></i>
              </span>
              <div>
                <h2 className="font-label text-base font-medium text-foreground-900">还有别的问题？</h2>
                <p className="mt-1 text-sm leading-relaxed text-foreground-600">
                  欢迎写信到 <strong className="font-label text-foreground-800">{site.email}</strong>
                  ，也可以先看看关于我们页面，了解我们的筛选标准。
                </p>
              </div>
            </div>
            <Link
              to="/about"
              className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              了解我们
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}